class ShippingCutoffCountdown extends HTMLElement {
  constructor() {
    super();
    this.timezone = this.dataset.timezone || 'Europe/Copenhagen';
    this.cutoffHour = parseInt(this.dataset.cutoffHour) || 14;
    this.cutoffMinute = parseInt(this.dataset.cutoffMinute) || 0;
    this.minimumHours = parseInt(this.dataset.minimumHours) || 6;
    this.updateInterval = null;
    this.countdownWrapper = null;
    this.tomorrowWrapper = null;
    this.globalCountdown = null;
  }

  connectedCallback() {
    this.countdownWrapper = this.querySelector('[data-countdown-state="countdown"]');
    this.tomorrowWrapper = this.querySelector('[data-countdown-state="tomorrow"]');
    this.countdownContainer = this.querySelector('.shipping-cutoff__countdown-container');

    this.checkAndRender();
    this.startCountdown();

    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  disconnectedCallback() {
    this.stopCountdown();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopCountdown();
    } else {
      this.checkAndRender();
      this.startCountdown();
    }
  }

  getCopenhagenComponents() {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short'
    });

    const parts = formatter.formatToParts(now);
    const components = {};

    parts.forEach(({ type, value }) => {
      components[type] = value;
    });

    return components;
  }

  getCopenhagenDate() {
    const cph = this.getCopenhagenComponents();
    return new Date(`${cph.year}-${cph.month}-${cph.day}T${cph.hour}:${cph.minute}:${cph.second}`);
  }

  getDayOfWeek() {
    const cph = this.getCopenhagenComponents();
    const weekdayMap = {
      'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6
    };
    return weekdayMap[cph.weekday];
  }

  calculateTimeRemaining() {
    const cph = this.getCopenhagenComponents();
    const currentHour = parseInt(cph.hour);
    const currentMinute = parseInt(cph.minute);
    const currentSecond = parseInt(cph.second);

    const currentTotalSeconds = (currentHour * 3600) + (currentMinute * 60) + currentSecond;
    const cutoffTotalSeconds = (this.cutoffHour * 3600) + (this.cutoffMinute * 60);

    let secondsRemaining = cutoffTotalSeconds - currentTotalSeconds;

    if (secondsRemaining < 0) {
      secondsRemaining = 0;
    }

    const hours = Math.floor(secondsRemaining / 3600);
    const minutes = Math.floor((secondsRemaining % 3600) / 60);
    const seconds = secondsRemaining % 60;

    return { hours, minutes, seconds, totalSeconds: secondsRemaining };
  }

  shouldDisplay() {
    const dayOfWeek = this.getDayOfWeek();

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { display: false, reason: 'weekend' };
    }

    const { hours, totalSeconds } = this.calculateTimeRemaining();

    if (totalSeconds === 0) {
      const isNextDayBusinessDay = this.isNextDayBusinessDay(dayOfWeek);
      if (isNextDayBusinessDay) {
        return { display: true, state: 'tomorrow' };
      }
      return { display: false, reason: 'past_cutoff_weekend' };
    }

    if (hours >= this.minimumHours) {
      return { display: false, reason: 'too_early' };
    }

    return { display: true, state: 'countdown' };
  }

  isNextDayBusinessDay(currentDayOfWeek) {
    return currentDayOfWeek >= 1 && currentDayOfWeek <= 4;
  }

  calculateCutoffDate() {
    const cph = this.getCopenhagenComponents();
    const cutoffHourStr = String(this.cutoffHour).padStart(2, '0');
    const cutoffMinuteStr = String(this.cutoffMinute).padStart(2, '0');

    const dateString = `${cph.year}-${cph.month}-${cph.day}T${cutoffHourStr}:${cutoffMinuteStr}:00`;

    const testDate = new Date(`${cph.year}-${cph.month}-${cph.day}`);
    const isDST = this.isDSTActive(testDate);
    const offset = isDST ? '+02:00' : '+01:00';

    return `${dateString}${offset}`;
  }

  isDSTActive(date) {
    const dateStr = date.toLocaleString('en-US', {
      timeZone: this.timezone,
      timeZoneName: 'short'
    });

    return dateStr.includes('CEST');
  }

  checkAndRender() {
    const shouldDisplay = this.shouldDisplay();

    if (!shouldDisplay.display) {
      this.style.display = 'none';
      return;
    }

    this.style.display = 'block';

    if (shouldDisplay.state === 'countdown') {
      this.renderCountdown();
    } else if (shouldDisplay.state === 'tomorrow') {
      this.renderTomorrow();
    }
  }

  renderCountdown() {
    const { hours } = this.calculateTimeRemaining();
    const cutoffDateISO = this.calculateCutoffDate();

    if (this.countdownContainer) {
      if (!this.countdownContainer.querySelector('global-countdown')) {
        const countdown = document.createElement('global-countdown');

        countdown.setAttribute('data-target-date', cutoffDateISO);
        countdown.setAttribute('data-units', 'hours:minutes:seconds');
        countdown.setAttribute('data-text-only', 'true');
        countdown.setAttribute('data-zero-pad', 'true');
        countdown.setAttribute('data-hide-zero-units', 'true');
        countdown.setAttribute('data-i18n-hour', this.countdownContainer.dataset.i18nHour || 'Hour');
        countdown.setAttribute('data-i18n-hours', this.countdownContainer.dataset.i18nHours || 'Hours');
        countdown.setAttribute('data-i18n-minute', this.countdownContainer.dataset.i18nMinute || 'Minute');
        countdown.setAttribute('data-i18n-minutes', this.countdownContainer.dataset.i18nMinutes || 'Minutes');
        countdown.setAttribute('data-i18n-second', this.countdownContainer.dataset.i18nSecond || 'Second');
        countdown.setAttribute('data-i18n-seconds', this.countdownContainer.dataset.i18nSeconds || 'Seconds');
        countdown.setAttribute('role', 'timer');
        countdown.setAttribute('aria-live', 'polite');
        countdown.setAttribute('aria-atomic', 'true');
        countdown.setAttribute('aria-label', this.countdownContainer.dataset.ariaLabel || 'Shipping countdown');

        this.countdownContainer.appendChild(countdown);
      } else {
        const existingCountdown = this.countdownContainer.querySelector('global-countdown');
        const currentDate = existingCountdown.getAttribute('data-target-date');
        if (currentDate !== cutoffDateISO) {
          existingCountdown.setAttribute('data-target-date', cutoffDateISO);
        }
      }
    }

    const urgencyClass = hours < 1 ? 'shipping-cutoff--high-urgency' : 'shipping-cutoff--countdown';

    if (this.countdownWrapper) {
      this.countdownWrapper.className = `shipping-cutoff__countdown-wrapper ${urgencyClass}`;
      this.countdownWrapper.style.display = 'block';
    }

    if (this.tomorrowWrapper) {
      this.tomorrowWrapper.style.display = 'none';
    }

    this.dispatchEvent(new CustomEvent('shipping-cutoff:displayed', {
      bubbles: true,
      detail: {
        hoursRemaining: hours,
        state: 'countdown'
      }
    }));
  }

  renderTomorrow() {
    if (this.countdownWrapper) {
      this.countdownWrapper.style.display = 'none';
    }

    if (this.countdownContainer) {
      this.countdownContainer.innerHTML = '';
    }

    if (this.tomorrowWrapper) {
      this.tomorrowWrapper.className = 'shipping-cutoff__tomorrow-wrapper shipping-cutoff--tomorrow';
      this.tomorrowWrapper.style.display = 'block';
    }

    this.dispatchEvent(new CustomEvent('shipping-cutoff:displayed', {
      bubbles: true,
      detail: {
        state: 'tomorrow'
      }
    }));
  }

  startCountdown() {
    this.stopCountdown();

    this.updateInterval = setInterval(() => {
      this.checkAndRender();
    }, 30000);
  }

  stopCountdown() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
}

customElements.define('shipping-cutoff-countdown', ShippingCutoffCountdown);
